import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Brain, ChevronRight, ChevronLeft, Sparkles, Star, Trophy } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { completeMission } from '../../utils/missionHelper';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fun personality quiz questions for middle schoolers
const quizQuestions = [
  {
    id: 1,
    question: "It's a rainy Saturday. What sounds the most fun?",
    emoji: "🌧️",
    options: [
      { text: "Building something cool (Legos, crafts, etc.)", type: "builder" },
      { text: "Playing video games or watching videos", type: "tech" },
      { text: "Reading or writing stories", type: "creative" },
      { text: "Hanging out with friends (even virtually!)", type: "social" }
    ]
  },
  {
    id: 2,
    question: "You have a big group project. What role do you take?",
    emoji: "👥",
    options: [
      { text: "The leader who organizes everyone", type: "leader" },
      { text: "The creative one with cool ideas", type: "creative" },
      { text: "The one who does the research", type: "analytical" },
      { text: "The one who makes sure everyone gets along", type: "social" }
    ]
  },
  {
    id: 3,
    question: "What kind of YouTube videos do you watch most?",
    emoji: "📺",
    options: [
      { text: "Gaming or tech stuff", type: "tech" },
      { text: "DIY, art, or cooking", type: "builder" },
      { text: "Funny videos and vlogs", type: "social" },
      { text: "Science experiments or documentaries", type: "analytical" }
    ]
  },
  {
    id: 4,
    question: "Your friend is upset about something. What do you do?",
    emoji: "💭",
    options: [
      { text: "Listen and try to help them feel better", type: "social" },
      { text: "Try to solve their problem", type: "analytical" },
      { text: "Distract them with something fun", type: "creative" },
      { text: "Give them space but check in later", type: "leader" }
    ]
  },
  {
    id: 5,
    question: "What sounds like the coolest field trip?",
    emoji: "🚌",
    options: [
      { text: "A tech company or robotics lab", type: "tech" },
      { text: "An art museum or theater", type: "creative" },
      { text: "A science center or zoo", type: "analytical" },
      { text: "A community service project", type: "social" }
    ]
  },
  {
    id: 6,
    question: "If you could have a superpower, which would you pick?",
    emoji: "⚡",
    options: [
      { text: "Super intelligence", type: "analytical" },
      { text: "Ability to create anything", type: "builder" },
      { text: "Mind reading (know what others think)", type: "social" },
      { text: "Time travel", type: "creative" }
    ]
  }
];

// Personality type results
const personalityResults = {
  tech: {
    title: "The Tech Wizard 🧙‍♂️",
    description: "You love technology, games, and figuring out how things work! You might enjoy careers in coding, game design, or engineering.",
    careers: ["Game Developer", "Software Engineer", "Robotics Designer", "IT Specialist"],
    color: "from-blue-500 to-cyan-500"
  },
  creative: {
    title: "The Creative Dreamer 🎨",
    description: "Your imagination is incredible! You love stories, art, and coming up with new ideas. The world needs your creativity!",
    careers: ["Artist", "Writer", "Filmmaker", "Designer", "Architect"],
    color: "from-purple-500 to-pink-500"
  },
  analytical: {
    title: "The Super Solver 🔍",
    description: "You love puzzles, research, and figuring things out! You're great at asking questions and finding answers.",
    careers: ["Scientist", "Doctor", "Detective", "Engineer", "Researcher"],
    color: "from-green-500 to-emerald-500"
  },
  social: {
    title: "The People Person 💝",
    description: "You're amazing with people! You care about others and love working with friends. You make everyone feel included.",
    careers: ["Teacher", "Counselor", "Nurse", "Social Worker", "Manager"],
    color: "from-orange-500 to-red-500"
  },
  builder: {
    title: "The Maker 🛠️",
    description: "You love creating things with your hands! Whether it's building, cooking, or crafting - you make cool stuff happen.",
    careers: ["Chef", "Carpenter", "Mechanic", "Construction", "Product Designer"],
    color: "from-amber-500 to-orange-500"
  },
  leader: {
    title: "The Natural Leader 👑",
    description: "People look up to you! You're great at organizing, planning, and helping others succeed. You take charge!",
    careers: ["Business Owner", "Manager", "Coach", "Event Planner", "CEO"],
    color: "from-indigo-500 to-purple-500"
  }
};

export default function MSQuizPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [resultType, setResultType] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleAnswer = (type) => {
    setAnswers(prev => ({ ...prev, [currentQuestion]: type }));
    
    if (currentQuestion < quizQuestions.length - 1) {
      setTimeout(() => setCurrentQuestion(prev => prev + 1), 300);
    } else {
      // Calculate result
      calculateResult();
    }
  };

  const calculateResult = () => {
    const typeCounts = {};
    Object.values(answers).forEach(type => {
      typeCounts[type] = (typeCounts[type] || 0) + 1;
    });
    
    // Add the last answer
    const lastAnswer = answers[currentQuestion];
    if (lastAnswer) {
      typeCounts[lastAnswer] = (typeCounts[lastAnswer] || 0) + 1;
    }
    
    // Find dominant type
    const dominant = Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0];
    setResultType(dominant ? dominant[0] : 'creative');
    setShowResults(true);
    
    // Save results
    saveResults(dominant ? dominant[0] : 'creative');
  };

  const saveResults = async (type) => {
    setSaving(true);
    try {
      await axios.post(`${API}/quiz/personality-result`, {
        personality_type: type,
        quiz_type: 'middle_school_personality',
        answers: answers
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quiz results saved!');
      completeMission(token, 'quiz');
    } catch (error) {
      console.error('Failed to save quiz results');
    } finally {
      setSaving(false);
    }
  };

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const result = resultType ? personalityResults[resultType] : null;

  if (showResults && result) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="mb-6">
            <motion.div
              initial={{ rotate: 0 }}
              animate={{ rotate: 360 }}
              transition={{ duration: 1 }}
              className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center mb-4"
            >
              <Trophy className="w-10 h-10 text-white" />
            </motion.div>
            <h1 className="text-3xl font-bold text-white mb-2">Quiz Complete!</h1>
            <p className="text-slate-300">Here's what we learned about you...</p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className={`p-8 rounded-2xl bg-gradient-to-br ${result.color} mb-6`}
          >
            <h2 className="text-3xl font-bold text-white mb-3">{result.title}</h2>
            <p className="text-white/90 text-lg mb-4">{result.description}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="glass-card p-6 mb-6"
          >
            <h3 className="text-white font-semibold mb-3 flex items-center justify-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-400" />
              Careers You Might Love:
            </h3>
            <div className="flex flex-wrap justify-center gap-2">
              {result.careers.map((career, i) => (
                <span
                  key={i}
                  className="px-4 py-2 bg-slate-700 rounded-full text-white"
                >
                  {career}
                </span>
              ))}
            </div>
          </motion.div>

          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => {
                setCurrentQuestion(0);
                setAnswers({});
                setShowResults(false);
                setResultType(null);
              }}
              variant="outline"
              className="border-slate-600 text-slate-300"
            >
              Take Again
            </Button>
            <Button
              onClick={() => navigate('/ms/careers')}
              className="bg-blue-500 hover:bg-blue-600"
            >
              Explore Careers
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  const question = quizQuestions[currentQuestion];

  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Brain className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Personality Quiz</h1>
            <p className="text-slate-300">Discover what makes YOU awesome!</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
        <p className="text-slate-300 text-sm mt-2">
          Question {currentQuestion + 1} of {quizQuestions.length}
        </p>
      </motion.div>

      {/* Question */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentQuestion}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          className="glass-card p-6"
        >
          <div className="text-center mb-6">
            <span className="text-5xl mb-4 block">{question.emoji}</span>
            <h2 className="text-xl font-semibold text-white">{question.question}</h2>
          </div>

          <div className="space-y-3">
            {question.options.map((option, index) => {
              const colors = [
                'from-blue-500 to-cyan-500',
                'from-purple-500 to-pink-500', 
                'from-green-500 to-emerald-500',
                'from-orange-500 to-red-500'
              ];
              return (
                <motion.button
                  key={index}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleAnswer(option.type)}
                  className={`w-full p-4 rounded-xl text-left transition-all ${
                    answers[currentQuestion] === option.type
                      ? `bg-gradient-to-r ${colors[index % 4]} text-white shadow-lg`
                      : 'bg-gradient-to-r from-slate-700 to-slate-600 text-white hover:from-slate-600 hover:to-slate-500'
                  }`}
                  data-testid={`quiz-option-${index}`}
                >
                  <span className="font-medium">{option.text}</span>
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      {currentQuestion > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-4"
        >
          <Button
            variant="ghost"
            onClick={() => setCurrentQuestion(prev => prev - 1)}
            className="text-slate-300 hover:text-white"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Back
          </Button>
        </motion.div>
      )}
    </div>
  );
}
